import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Surface } from "../../atoms/Surface";
import { Spinner } from "../../atoms/Spinner";
import { useNavigate } from "react-router-dom";
import useAuth from "../../../hooks/useAuth";
import { useGetEntityOwnerData } from "../../../hooks/entity";
import { forgetOwnerData } from "../../../hooks/entity/ownerData";
import { CustomButton } from "../../atoms/CustomButton";
import { useTranslation } from "react-i18next";
import { HiCheckCircle } from "react-icons/hi";
import HttpService from "../../../services/http";
import { toast } from "react-toastify";
import { useSearchWithSecrets } from "../../../utils/addressSecrets";

const CreditResult = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const searchParams = useSearchWithSecrets();
  const { user, customer_identification } = useAuth();

  const planName = searchParams.get("plan-name") || "SUMOC_SMSGTO20USD";
  const isWhatsapp = planName === "SUMOC_WAGTO20USD";
  const creditKey = isWhatsapp ? "whatsapp_credit" : "sms_credit";
  const {
    data: entityData,
    isLoading: isLoadingData,
    error: dataError,
    refetch,
  } = useGetEntityOwnerData(
    customer_identification,
    user.entity_identification,
    "user_limit"
  );

  const [isProcessing, setIsProcessing] = React.useState(false);
  const [hasProcessed, setHasProcessed] = React.useState(false);
  const [isSuccess, setIsSuccess] = React.useState(false);

  useGetEntityOwnerData(
    customer_identification,
    user.entity_identification,
    creditKey,
    {
      enabled: isSuccess,
    }
  );

  useEffect(() => {
    const verifyCreditPayment = async () => {
      if (!entityData || hasProcessed || isProcessing) return;

      const userIdentification = searchParams.get("user-identification");

      if (!userIdentification) {
        console.error("Missing user-identification parameter");
        navigate("/control-panel", { replace: true });
        return;
      }

      setIsProcessing(true);
      setHasProcessed(true);

      const verifyEndpoint = isWhatsapp
        ? "verify-whatsapp-credit-payment"
        : "verify-sms-credit-payment";

      try {
        await HttpService.post(
          `/${userIdentification}/${user.entity_identification}/billing/${planName}/${verifyEndpoint}`,
          {}
        ).then(() => {
          forgetOwnerData(
            customer_identification,
            user.entity_identification,
            creditKey
          );
          setIsSuccess(true);
        });

        setTimeout(() => {
          navigate("/control-panel", { replace: true });
        }, 4000);
      } catch (error: any) {
        console.error("Error verifying credit payment:", error);
        toast.error(
          error?.response?.data?.message ||
            t(
              "dashboard.entities.viewEntityUser.creditResult.errorToastMessage"
            )
        );
        setTimeout(() => {
          navigate("/control-panel", { replace: true });
        }, 4000);
      } finally {
        setIsProcessing(false);
      }
    };

    verifyCreditPayment();
  }, [
    creditKey,
    entityData,
    hasProcessed,
    isProcessing,
    isWhatsapp,
    navigate,
    planName,
    searchParams,
    t,
    user.entity_identification,
  ]);

  if (dataError)
    return (
      <CustomButton
        variant="primary"
        onClick={() => refetch()}
        title={t("dashboard.UserDetailsSpace.btn-01") as string}
      />
    );

  return (
    <div className="roi-guarantee-success-page">
      <Helmet>
        <title>
          {t(
            isWhatsapp
              ? "dashboard.entities.viewEntityUser.creditResult.whatsappPageTitle"
              : "dashboard.entities.viewEntityUser.creditResult.pageTitle"
          )}{" "}
          | Super User Manager
        </title>
        <meta
          name="description"
          content={
            t(
              isWhatsapp
                ? "dashboard.entities.viewEntityUser.creditResult.whatsappPageDescription"
                : "dashboard.entities.viewEntityUser.creditResult.pageDescription"
            ) || ""
          }
        />
      </Helmet>

      <div className="container mx-auto py-6 pt-20">
        <div className="max-w-4xl mx-auto">
          {!isSuccess ? (
            <Surface className="mb-8 bg-blue-50 border-blue-200">
              <div className="text-center py-8">
                <div className="relative mx-auto mb-6 w-16 h-16">
                  <Spinner size="xl" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-4">
                  {t(
                    "dashboard.entities.viewEntityUser.creditResult.processingTitle",
                    "Processing Your Payment"
                  )}
                </h1>
                <p className="text-gray-600 max-w-md mx-auto mb-6">
                  {t(
                    isWhatsapp
                      ? "dashboard.entities.viewEntityUser.creditResult.whatsappProcessingDescription"
                      : "dashboard.entities.viewEntityUser.creditResult.processingDescription"
                  )}
                </p>
                <div className="flex justify-center gap-1">
                  <span
                    className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                    style={{ animationDelay: "0ms" }}
                  ></span>
                  <span
                    className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                    style={{ animationDelay: "150ms" }}
                  ></span>
                  <span
                    className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"
                    style={{ animationDelay: "300ms" }}
                  ></span>
                </div>
              </div>
            </Surface>
          ) : (
            <Surface className="mb-8 bg-green-50 border-green-200">
              <div className="text-center py-8">
                <HiCheckCircle className="w-16 h-16 text-green-600 mx-auto mb-6" />
                <h1 className="text-2xl font-bold text-gray-900 mb-4">
                  {t(
                    isWhatsapp
                      ? "dashboard.entities.viewEntityUser.creditResult.whatsappTitle"
                      : "dashboard.entities.viewEntityUser.creditResult.title"
                  )}
                </h1>

                <p className="text-lg text-gray-600 max-w-2xl mx-auto mt-4">
                  {t(
                    isWhatsapp
                      ? "dashboard.entities.viewEntityUser.creditResult.whatsappDescription"
                      : "dashboard.entities.viewEntityUser.creditResult.description"
                  )}
                </p>

                {(isProcessing || isLoadingData) && <Spinner />}
              </div>
            </Surface>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditResult;
